/**
 * Shared contract for the Project Documents feature (ZPL-201).
 *
 * Documents can be scoped to a project or a property. Upload uses a
 * three-step flow: create (signed GCS target) → browser PUT bytes → confirm.
 * A per-document `aiReadable` flag gates the `mareczek-ai` system actor at
 * the loader. Authorization is a dedicated CASL `Document` subject.
 */

export const DOCUMENT_STATUSES = ["pending_upload", "ready"] as const
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number]

/**
 * Semantic document type chosen by the uploader (ZPL-263) — distinct from
 * `kind` (the MIME-derived file format). Immutable after upload. The type
 * selects the AI job: reviewable types (`contract` — ZPL-263, `land_report`
 * — ZPL-331) each run their own AI review prompt (see the api's
 * DOCUMENT_REVIEW_CONFIGS); the others terminate the AI early. `contract`
 * stays first ([0] is the migration backfill default), `other` stays last
 * (the picker's catch-all).
 */
export const DOCUMENT_TYPES = [
	"contract",
	"agreement",
	"notice",
	"bill",
	"land_report",
	"other",
] as const
export type DocumentType = (typeof DOCUMENT_TYPES)[number]

// Upload allowlist (MIME → friendly kind for the icon). 50 MB cap.
export const DOCUMENT_CONTENT_TYPES = {
	"application/pdf": "pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
		"doc",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "sheet",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation":
		"slides",
	"image/png": "image",
	"image/jpeg": "image",
	"image/webp": "image",
	"text/plain": "text",
	"text/csv": "text",
} as const
export type DocumentKind =
	(typeof DOCUMENT_CONTENT_TYPES)[keyof typeof DOCUMENT_CONTENT_TYPES]
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024
/**
 * Upper bound on a document's stored size for AI contract review (ZPL-203).
 * Deliberately smaller than the 50 MB upload cap and pinned to the most
 * restrictive among the current top models (Claude ~32 MB) so the review model
 * can be swapped without breaking eligibility. Documents above this still
 * upload fine but the review job early-terminates with DOCUMENT_TOO_LARGE
 * before any LLM call.
 */
export const AI_REVIEW_MAX_BYTES = 30 * 1024 * 1024
export function documentKindForContentType(ct: string): DocumentKind | null {
	return (DOCUMENT_CONTENT_TYPES as Record<string, DocumentKind>)[ct] ?? null
}

/**
 * Content types the AI can read (contract review ZPL-203, invoice extraction
 * ZPL-223): PDFs via the model's native document path, images via its native
 * vision input — both with no third-party reader. The upload allowlist also
 * permits docx/xlsx/pptx/text, which upload fine but are not AI-processed;
 * the job/UI surface a clear unsupported state for those.
 */
export const AI_REVIEW_CONTENT_TYPES = [
	"application/pdf",
	"image/png",
	"image/jpeg",
	"image/webp",
] as const
export function isAiReviewableContentType(ct: string): boolean {
	return (AI_REVIEW_CONTENT_TYPES as readonly string[]).includes(ct)
}

export type DocumentScopeType = "project" | "property"
export interface DocumentScope {
	type: DocumentScopeType
	id: string
}

export interface DocumentOwnerDTO {
	actorType: "user" | "ai"
	actorId: string
}
export interface DocumentDTO {
	id: string
	scope: DocumentScope
	title: string
	originalFilename: string | null
	mimeType: string
	kind: DocumentKind
	sizeBytes: number | null
	aiReadable: boolean
	documentType: DocumentType
	status: DocumentStatus
	owner: DocumentOwnerDTO
	canDownload: boolean
	canManageGrants: boolean
	/**
	 * Number of EXPLICIT access grants on the document (ZPL-332) — the share
	 * badge's count. Owners/managers who see the document via role rather
	 * than a grant are not counted: the badge answers "how many people did I
	 * share this with", matching the share modal's granted list.
	 */
	grantCount: number
	downloadUrl?: string // present only when canDownload
	createdAt: string
}
export interface CreateDocumentBody {
	title: string
	contentType: string
	filename: string
	declaredSize: number
	documentType: DocumentType
	aiReadable?: boolean
}
export interface DocumentUploadTarget {
	uploadUrl: string
	method: "PUT" | "POST"
	headers?: Record<string, string>
	params?: Record<string, string>
}
export interface DocumentUploadResponse {
	document: DocumentDTO
	upload: DocumentUploadTarget
	expiresAt: string
}
export interface DocumentGrantDTO {
	userId: string
	displayName: string
	createdAt: string
}
export interface DocumentGrantCandidateDTO {
	userId: string
	displayName: string
	granted: boolean
	isOwner: boolean
}
